import React, { useState, useEffect } from 'react';
import styles from './FoodPage.module.scss';
import { getFoodCategories, getFoodItems, createFoodOrder } from '../../../service/FoodService';
import Loader from '../../../components/Loader/Loader';
import { PizzaIcon, ShoppingCartIcon, SearchIcon, CheckIcon, LockIcon, FireIcon, ClockIcon, TrashIcon } from '../../../components/CustomIcons';

const FoodPage = ({ embedded = false, goBack }) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [foodItems, setFoodItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [phone, setPhone] = useState('');
  
  const isAuthenticated = !!localStorage.getItem('token');

  useEffect(() => {
    fetchCategories();
    loadCart();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchFoodItems(selectedCategory.id);
    }
  }, [selectedCategory]);

  const loadCart = () => {
    const savedCart = localStorage.getItem('foodCart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (err) {
        setCart([]);
      }
    }
  };

  const saveCart = (newCart) => {
    setCart(newCart);
    localStorage.setItem('foodCart', JSON.stringify(newCart));
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await getFoodCategories();
      setCategories(response.data || []);
      setError(null);
    } catch (err) {
      setError('Ошибка загрузки категорий');
    } finally {
      setLoading(false);
    }
  };

  const fetchFoodItems = async (categoryId) => {
    try {
      setLoading(true);
      const response = await getFoodItems({ category: categoryId });
      setFoodItems(response.data?.results || response.data || []);
      setError(null);
    } catch (err) {
      setError('Ошибка загрузки блюд');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (item) => {
    if (!isAuthenticated) {
      return;
    }

    const existingItem = cart.find(cartItem => cartItem.id === item.id);
    if (existingItem) {
      saveCart(cart.map(cartItem =>
        cartItem.id === item.id
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      ));
    } else {
      saveCart([...cart, { ...item, quantity: 1 }]);
    }
  };

  const removeFromCart = (itemId) => {
    saveCart(cart.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    saveCart(cart.map(item =>
      item.id === itemId
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2);
  };

  const getCartItemsCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      return;
    }

    if (!phone.trim()) {
      setError('Укажите телефон для связи');
      return;
    }

    const totalAmount = getTotalPrice();

    try {
      setProcessingOrder(true);
      setError(null);
      await createFoodOrder({
        items_data: cart.map(item => ({
          food_item: item.id,
          quantity: item.quantity
        })),
        phone: phone,
        total_amount: totalAmount,
        notes: 'Заказ из веб-приложения'
      });

      setOrderSuccess(true);
      saveCart([]);
      setShowCart(false);
      setPhone('');

      setTimeout(() => {
        setOrderSuccess(false);
      }, 5000);
    } catch (err) {
      setError(err.response?.data?.phone?.[0] || err.response?.data?.total_amount?.[0] || 'Ошибка создания заказа');
    } finally {
      setProcessingOrder(false);
    }
  };

  const getCategoryImage = (category) => {
    if (category.image) return category.image;
    
    const defaultImages = {
      'Супы': 'https://via.placeholder.com/300x200/FF6B6B/ffffff?text=Супы',
      'Салаты': 'https://via.placeholder.com/300x200/4ECDC4/ffffff?text=Салаты',
      'Горячее': 'https://via.placeholder.com/300x200/FFE66D/ffffff?text=Горячее',
      'Десерты': 'https://via.placeholder.com/300x200/FF6B9D/ffffff?text=Десерты',
      'Напитки': 'https://via.placeholder.com/300x200/95E1D3/ffffff?text=Напитки'
    };
    
    return defaultImages[category.name] || 'https://via.placeholder.com/300x200/9E9E9E/ffffff?text=Еда';
  };

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFoodItems = foodItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && categories.length === 0) {
    return (
      <div className={styles.foodPage}>
        <div className={styles.loadingSpinner}>
          <Loader />
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  if (error && categories.length === 0) {
    return (
      <div className={styles.foodPage}>
        <div className={styles.errorContainer}>
          <h3>{error}</h3>
          <button onClick={fetchCategories} className={styles.retryButton}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (orderSuccess) {
    return (
      <div className={styles.foodPage}>
        <div className={styles.successMessage}>
          <div className={styles.successIcon}><CheckIcon size={48} /></div>
          <h2>Заявка успешно оставлена!</h2>
          <p>Скоро с вами свяжутся наши менеджеры для подтверждения заказа</p>
          <button onClick={() => {
            setOrderSuccess(false);
            setSelectedCategory(null);
          }} className={styles.backButton}>
            Вернуться к меню
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.foodPage}>
      <div className={styles.foodHeader}>
        <h1><PizzaIcon size={32} style={{ verticalAlign: 'middle', marginRight: '10px' }} /> Доставка еды</h1>
        <p className={styles.foodSubtitle}>
          Выберите вкусные блюда из нашего меню
        </p>
      </div>

      {!isAuthenticated && (
        <div className={styles.authWarning}>
          <div className={styles.authWarningIcon}><LockIcon size={32} /></div>
          <div className={styles.authWarningContent}>
            <h3>Требуется авторизация для заказа</h3>
            <p>Для добавления блюд в корзину и оформления заказа необходимо войти в аккаунт</p>
          </div>
        </div>
      )}

      {!selectedCategory ? (
        <>
          <div className={styles.foodControls}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Поиск категорий..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className={styles.categoriesGrid}>
            {filteredCategories.length === 0 ? (
              <div className={styles.noResults}>
                <p>Категории не найдены</p>
              </div>
            ) : (
              filteredCategories.map(category => (
                <div
                  key={category.id}
                  className={styles.categoryCard}
                  onClick={() => setSelectedCategory(category)}
                >
                  <img 
                    src={getCategoryImage(category)} 
                    alt={category.name}
                    className={styles.categoryImage}
                  />
                  <div className={styles.categoryContent}>
                    <h3>{category.name}</h3>
                    {category.description && (
                      <p className={styles.categoryDescription}>{category.description}</p>
                    )}
                    <button className={styles.viewItemsBtn}>
                      Смотреть блюда →
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        <div className={styles.itemsSection}>
          <div className={styles.itemsHeader}>
            <button
              className={styles.backButton}
              onClick={() => {
                setSelectedCategory(null);
                setFoodItems([]);
              }}
            >
              ← Назад к категориям
            </button>
            <h2>{selectedCategory.name}</h2>
          </div>

          <div className={styles.foodControls}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Поиск блюд..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className={styles.loadingSpinner}>
              <Loader />
              <p>Загрузка блюд...</p>
            </div>
          ) : (
            <div className={styles.itemsGrid}>
              {filteredFoodItems.length === 0 ? (
                <div className={styles.noResults}>
                  <p>Блюда не найдены</p>
                </div>
              ) : (
                filteredFoodItems.map(item => (
                  <div
                    key={item.id}
                    className={`${styles.itemCard} ${!item.is_available ? styles.unavailable : ''}`}
                  >
                    {item.image && (
                      <div className={styles.itemImageWrapper}>
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className={styles.itemImage}
                        />
                        <div className={styles.itemBadges}>
                          {item.is_vegetarian && (
                            <span className={styles.vegBadge}>🌱</span>
                          )}
                          {item.is_spicy && (
                            <span className={styles.spicyBadge}>🌶️</span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className={styles.itemContent}>
                      <h4>{item.name}</h4>
                      
                      {item.description && (
                        <p className={styles.itemDescription}>{item.description}</p>
                      )}

                      {item.ingredients && (
                        <p className={styles.itemIngredients}>
                          <strong>Состав:</strong> {item.ingredients}
                        </p>
                      )}

                      <div className={styles.itemMeta}>
                        {item.weight && (
                          <span className={styles.metaItem}>
                            {item.weight}г
                          </span>
                        )}
                        {item.calories && (
                          <span className={styles.metaItem}>
                            <FireIcon size={14} style={{ marginRight: '4px' }} /> {item.calories} ккал
                          </span>
                        )}
                        {item.cooking_time && (
                          <span className={styles.metaItem}>
                            <ClockIcon size={14} style={{ marginRight: '4px' }} /> {item.cooking_time} мин
                          </span>
                        )}
                      </div>

                      <div className={styles.itemFooter}>
                        <span className={styles.itemPrice}>{parseInt(item.price).toLocaleString('ru-RU')} сом</span>
                        <button
                          className={styles.addToCartBtn}
                          onClick={() => addToCart(item)}
                          disabled={!item.is_available}
                        >
                          {!isAuthenticated
                            ? <><LockIcon size={16} style={{ marginRight: '4px' }} /> Требуется авторизация</>
                            : !item.is_available
                            ? 'Недоступно'
                            : 'В корзину'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Плавающая кнопка корзины */}
      {cart.length > 0 && (
        <button
          className={styles.floatingCartBtn}
          onClick={() => setShowCart(!showCart)}
        >
          <svg className={styles.cartIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 3H5.5L5.6 3.5M7 13H17L21 5H6.5M7 13L5.6 3.5M7 13L5.5 15.5M17 13L18.5 15.5M7.5 18.5C7.5 19.0523 7.05228 19.5 6.5 19.5C5.94772 19.5 5.5 19.0523 5.5 18.5C5.5 17.9477 5.94772 17.5 6.5 17.5C7.05228 17.5 7.5 17.9477 7.5 18.5ZM19 18.5C19 19.0523 18.5523 19.5 18 19.5C17.4477 19.5 17 19.0523 17 18.5C17 17.9477 17.4477 17.5 18 17.5C18.5523 17.5 19 17.9477 19 18.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className={styles.cartCount}>{getCartItemsCount()}</span>
          <span className={styles.cartTotal}>{parseInt(getTotalPrice()).toLocaleString('ru-RU')} сом</span>
        </button>
      )}

      {showCart && cart.length > 0 && (
        <div className={styles.cartOverlay} onClick={() => setShowCart(false)}>
          <div className={styles.cartModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.cartHeader}>
              <h3><ShoppingCartIcon size={24} style={{ verticalAlign: 'middle', marginRight: '8px' }} /> Корзина</h3>
              <button className={styles.closeCart} onClick={() => setShowCart(false)}>
                ✕
              </button>
            </div>

            <div className={styles.cartItems}>
              {cart.map(item => (
                <div key={item.id} className={styles.cartItem}>
                  <div className={styles.cartItemInfo}>
                    <h4>{item.name}</h4>
                    <p className={styles.cartItemMeta}>
                      {item.weight}г • {item.price} сом
                    </p>
                  </div>
                  
                  <div className={styles.cartItemControls}>
                    <div className={styles.quantityControls}>
                      <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>
                        −
                      </button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                        +
                      </button>
                    </div>
                    
                    <div className={styles.cartItemPrice}>
                      {parseInt(item.price * item.quantity).toLocaleString('ru-RU')} сом
                    </div>
                    
                    <button
                      className={styles.removeItemBtn}
                      onClick={() => removeFromCart(item.id)}
                    >
                      <TrashIcon size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.cartFooter}>
              <div className={styles.cartTotal}>
                <span>Итого:</span>
                <span className={styles.totalPrice}>{parseInt(getTotalPrice()).toLocaleString('ru-RU')} сом</span>
              </div>
              {isAuthenticated ? (
                <>
                  <div className={styles.phoneInputGroup}>
                    <label htmlFor="phone">Телефон для связи:</label>
                    <input
                      id="phone"
                      type="tel"
                      placeholder="+996 XXX XXX XXX"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className={styles.phoneInput}
                    />
                  </div>
                  <button 
                    className={styles.checkoutBtn} 
                    onClick={handleCheckout}
                    disabled={processingOrder}
                  >
                    {processingOrder ? 'Обработка...' : 'Оставить заявку'}
                  </button>
                </>
              ) : (
                <div className={styles.authRequired}>
                  <div className={styles.authRequiredIcon}><LockIcon size={48} /></div>
                  <div className={styles.authRequiredText}>
                    <h4>Требуется авторизация</h4>
                    <p>Войдите в систему для оформления заказа</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className={styles.errorMessage}>
          <p>{error}</p>
          <button onClick={fetchCategories}>Попробовать снова</button>
        </div>
      )}
    </div>
  );
};

export default FoodPage;